import type {
  Account,
  Approval,
  AuditEvent,
  Beneficiary,
  Card,
  Iban,
  Payment,
  PaymentAttempt,
  SmeCompanyView,
  Transaction,
  User,
} from '@shared/contracts/entities';
import type { SmeFeatureFlags } from '@shared/contracts/featureFlags';
import { SME_PERMISSIONS as P } from '@shared/contracts/permissions';
import { MODULES, type ModuleKey, type Programme, type Tenant } from '@/services/entitlements';

/**
 * Deterministic demo fixtures.
 *
 * Canonical IDs come from 09_DEMO_FIXTURE_AND_DATA_SCENARIOS.md. Every surface
 * must use these same IDs and statuses — "a local fixture fork is a defect".
 *
 * All monetary values are integer minor units (pence/cents), so no
 * floating-point drift can reach a displayed balance. All timestamps are fixed
 * relative to the deterministic clock epoch (2026-07-27T09:00:00Z).
 */

const EPOCH = Date.parse('2026-07-27T09:00:00.000Z');

/** Hours before the epoch, as an ISO string. Keeps fixtures readable. */
function hoursAgo(h: number): string {
  return new Date(EPOCH - h * 3_600_000).toISOString();
}
function daysAgo(d: number): string {
  return new Date(EPOCH - d * 86_400_000).toISOString();
}
function daysAhead(d: number): string {
  return new Date(EPOCH + d * 86_400_000).toISOString();
}

/* ------------------------------------------------------------- tenant ----- */

const ALL_MODULES: readonly ModuleKey[] = Object.values(MODULES);

export const TENANT: Tenant = {
  id: 'tenant_trb_demo',
  name: 'Tribe Bank',
  // Digital assets are deliberately not purchased: TT-R134 must render as a
  // provider-gated / not-entitled surface rather than inventing capability.
  purchasedModules: ALL_MODULES.filter((m) => m !== MODULES.digitalAssets),
  brandTokens: {
    '--color-primary': '#16382A',
    '--color-accent': '#B9E55C',
  },
};

export const PROGRAMME: Programme = {
  id: 'programme_uk_sme',
  name: 'UK SME Programme',
  tenantId: TENANT.id,
  enabledModules: ALL_MODULES.filter((m) => m !== MODULES.digitalAssets),
  domesticScheme: 'uk-faster-payments',
};

/* ------------------------------------------------------------ companies --- */

const COMPANY_MODULES: readonly ModuleKey[] = ALL_MODULES.filter(
  (m) => m !== MODULES.digitalAssets && m !== MODULES.invoicing,
);

export function buildCompanies(): SmeCompanyView[] {
  return [
    {
      id: 'company_healthy_ltd',
      legalName: 'Northgate Provisions Ltd',
      tradingName: 'Northgate',
      registrationNumber: '09482217',
      country: 'GB',
      baseCurrency: 'GBP',
      status: 'active',
      enabledModules: [...COMPANY_MODULES],
      accessibleAccountIds: ['acc_gbp_main', 'acc_eur_trade', 'acc_usd_supplier', 'acc_gbp_expense'],
      currentUserRoleId: 'role_owner',
    },
    {
      id: 'company_onboarding_ltd',
      legalName: 'Harbourline Studio Ltd',
      tradingName: 'Harbourline',
      registrationNumber: '13998204',
      country: 'GB',
      baseCurrency: 'GBP',
      status: 'onboarding',
      statusReason:
        'We still need proof of address for one director before payments can be enabled.',
      enabledModules: [MODULES.accounts, MODULES.documents, MODULES.notifications, MODULES.personalisation],
      accessibleAccountIds: ['acc_onb_gbp'],
      currentUserRoleId: 'role_owner',
    },
    {
      id: 'company_restricted_ltd',
      legalName: 'Copperfield Logistics Ltd',
      tradingName: 'Copperfield',
      registrationNumber: '10552390',
      country: 'GB',
      baseCurrency: 'GBP',
      status: 'restricted',
      statusReason:
        'Outgoing payments are paused while we complete a routine compliance review. You can still view balances and history.',
      enabledModules: [...COMPANY_MODULES],
      accessibleAccountIds: ['acc_res_gbp'],
      currentUserRoleId: 'role_owner',
    },
    {
      id: 'company_high_volume_ltd',
      legalName: 'Meridian Wholesale Ltd',
      tradingName: 'Meridian',
      registrationNumber: '08211764',
      country: 'GB',
      baseCurrency: 'GBP',
      status: 'active',
      enabledModules: [...COMPANY_MODULES],
      accessibleAccountIds: ['acc_hv_gbp'],
      currentUserRoleId: 'role_owner',
    },
  ];
}

/* ---------------------------------------------------------------- users --- */

const OWNER_PERMISSIONS = Object.values(P);

const FINANCE_PERMISSIONS = [
  P.accountsView,
  P.accountsViewSensitiveDetails,
  P.transactionsView,
  P.transactionsExport,
  P.beneficiariesView,
  P.beneficiariesCreate,
  P.beneficiariesManage,
  P.paymentsCreate,
  P.paymentsSubmit,
  P.paymentsCancel,
  P.cardsView,
  P.cardsManage,
  P.reportsView,
  P.reportsExport,
  P.teamView,
];

const APPROVER_PERMISSIONS = [
  P.accountsView,
  P.transactionsView,
  P.beneficiariesView,
  P.paymentsApprove,
  P.cardsView,
  P.reportsView,
  P.teamView,
];

const EMPLOYEE_PERMISSIONS = [P.accountsView, P.transactionsView, P.cardsView];

const READONLY_PERMISSIONS = [P.accountsView, P.transactionsView, P.reportsView, P.cardsView];

export function buildUsers(): User[] {
  const companyIds = [
    'company_healthy_ltd',
    'company_onboarding_ltd',
    'company_restricted_ltd',
    'company_high_volume_ltd',
  ];
  return [
    {
      id: 'user_owner',
      name: 'Priya Raman',
      email: 'priya@northgate.example',
      roleId: 'role_owner',
      companyIds,
      status: 'active',
      permissions: [...OWNER_PERMISSIONS],
      accountAccess: ['*'],
    },
    {
      id: 'user_finance',
      name: 'Daniel Okafor',
      email: 'daniel@northgate.example',
      roleId: 'role_finance',
      companyIds: ['company_healthy_ltd', 'company_high_volume_ltd'],
      status: 'active',
      permissions: [...FINANCE_PERMISSIONS],
      accountAccess: ['*'],
    },
    {
      id: 'user_approver',
      name: 'Helena Vasquez',
      email: 'helena@northgate.example',
      roleId: 'role_approver',
      companyIds: ['company_healthy_ltd'],
      status: 'active',
      permissions: [...APPROVER_PERMISSIONS],
      accountAccess: ['*'],
    },
    {
      id: 'user_employee',
      name: 'Tom Whitfield',
      email: 'tom@northgate.example',
      roleId: 'role_employee',
      companyIds: ['company_healthy_ltd'],
      status: 'active',
      permissions: [...EMPLOYEE_PERMISSIONS],
      // Only the expense account and their own card.
      accountAccess: ['acc_gbp_expense'],
    },
    {
      id: 'user_readonly',
      name: 'Grace Lindholm',
      email: 'grace@ledgerworks.example',
      roleId: 'role_readonly',
      companyIds: ['company_healthy_ltd'],
      status: 'active',
      permissions: [...READONLY_PERMISSIONS],
      accountAccess: ['*'],
      // Scheduled access: an accountant engaged for the quarter. Expires
      // mid-demo when the clock is advanced, proving TT/WR permission-window
      // behaviour without editing any fixture.
      permissionValidFrom: daysAgo(60),
      permissionValidUntil: daysAhead(3),
    },
  ];
}

/* ------------------------------------------------------------- accounts --- */

export function buildAccounts(): Account[] {
  return [
    {
      id: 'acc_gbp_main',
      companyId: 'company_healthy_ltd',
      displayName: 'Business current',
      currency: 'GBP',
      balance: 4_812_640,
      availableBalance: 4_612_640, // 2,000.00 reserved — available < booked on purpose
      status: 'active',
      accountType: 'business-current',
      localDetails: { sortCode: '040404', accountNumber: '71829304' },
      ibanIds: ['iban_gbp_main', 'iban_gbp_virtual_rent'],
    },
    {
      id: 'acc_eur_trade',
      companyId: 'company_healthy_ltd',
      displayName: 'EUR trade',
      currency: 'EUR',
      balance: 1_284_900,
      availableBalance: 1_284_900,
      status: 'active',
      accountType: 'business-current',
      ibanIds: ['iban_eur_trade'],
    },
    {
      id: 'acc_usd_supplier',
      companyId: 'company_healthy_ltd',
      displayName: 'USD supplier',
      currency: 'USD',
      balance: 512_300,
      availableBalance: 512_300,
      status: 'restricted',
      statusReason:
        'Receive only. This account can accept incoming payments but cannot send until the currency review completes.',
      accountType: 'business-current',
      ibanIds: [],
    },
    {
      id: 'acc_gbp_expense',
      companyId: 'company_healthy_ltd',
      displayName: 'Card & expenses',
      currency: 'GBP',
      balance: 348_120,
      availableBalance: 331_620,
      status: 'active',
      accountType: 'expense',
      localDetails: { sortCode: '040404', accountNumber: '71829399' },
      ibanIds: [],
    },
    {
      id: 'acc_onb_gbp',
      companyId: 'company_onboarding_ltd',
      displayName: 'Business current',
      currency: 'GBP',
      balance: 0,
      availableBalance: 0,
      status: 'pending',
      statusReason: 'Your account opens once identity checks are complete.',
      accountType: 'business-current',
      ibanIds: [],
    },
    {
      id: 'acc_res_gbp',
      companyId: 'company_restricted_ltd',
      displayName: 'Business current',
      currency: 'GBP',
      balance: 1_902_450,
      availableBalance: 1_902_450,
      status: 'suspended',
      statusReason:
        'Payments from this account are suspended pending compliance review. History remains available.',
      accountType: 'business-current',
      localDetails: { sortCode: '040404', accountNumber: '55120987' },
      ibanIds: ['iban_res_gbp'],
    },
    {
      id: 'acc_hv_gbp',
      companyId: 'company_high_volume_ltd',
      displayName: 'Operating account',
      currency: 'GBP',
      balance: 28_400_990,
      availableBalance: 28_400_990,
      status: 'active',
      accountType: 'business-current',
      localDetails: { sortCode: '040404', accountNumber: '33019876' },
      ibanIds: ['iban_hv_gbp'],
    },
  ];
}

export function buildIbans(): Iban[] {
  return [
    {
      id: 'iban_gbp_main',
      accountIds: ['acc_gbp_main'],
      iban: 'GB29TRBK04040471829304',
      bic: 'TRBKGB2L',
      country: 'GB',
      currency: 'GBP',
      status: 'active',
      holderName: 'Northgate Provisions Ltd',
      label: 'Primary',
    },
    {
      id: 'iban_gbp_virtual_rent',
      accountIds: ['acc_gbp_main'],
      iban: 'GB61TRBK04040471829377',
      bic: 'TRBKGB2L',
      country: 'GB',
      currency: 'GBP',
      status: 'active',
      holderName: 'Northgate Provisions Ltd',
      isVirtual: true,
      label: 'Rent collections',
    },
    {
      id: 'iban_eur_trade',
      accountIds: ['acc_eur_trade'],
      iban: 'DE89370400440532013000',
      bic: 'TRBKDEFF',
      country: 'DE',
      currency: 'EUR',
      status: 'active',
      holderName: 'Northgate Provisions Ltd',
      label: 'SEPA',
    },
    {
      id: 'iban_res_gbp',
      accountIds: ['acc_res_gbp'],
      iban: 'GB18TRBK04040455120987',
      bic: 'TRBKGB2L',
      country: 'GB',
      currency: 'GBP',
      status: 'suspended',
      statusReason: 'Suspended alongside the account during compliance review.',
      holderName: 'Copperfield Logistics Ltd',
    },
    {
      id: 'iban_hv_gbp',
      accountIds: ['acc_hv_gbp'],
      iban: 'GB44TRBK04040433019876',
      bic: 'TRBKGB2L',
      country: 'GB',
      currency: 'GBP',
      status: 'active',
      holderName: 'Meridian Wholesale Ltd',
    },
  ];
}

/* -------------------------------------------------------- beneficiaries --- */

export function buildBeneficiaries(): Beneficiary[] {
  return [
    {
      id: 'ben_exact',
      companyId: 'company_healthy_ltd',
      name: 'Ashgrove Packaging Ltd',
      type: 'business',
      country: 'GB',
      currency: 'GBP',
      accountNumber: '40028871',
      sortCode: '203045',
      verificationResult: 'match',
      verificationCheckedAt: daysAgo(12),
      status: 'active',
    },
    {
      id: 'ben_close',
      companyId: 'company_healthy_ltd',
      name: 'J. Fairweather Haulage',
      type: 'business',
      country: 'GB',
      currency: 'GBP',
      accountNumber: '61200934',
      sortCode: '601613',
      // Name on file at the bank is "John Fairweather Haulage Limited".
      verificationResult: 'close-match',
      verificationCheckedAt: daysAgo(3),
      status: 'active',
    },
    {
      id: 'ben_nomatch',
      companyId: 'company_healthy_ltd',
      name: 'Delta Components',
      type: 'business',
      country: 'GB',
      currency: 'GBP',
      accountNumber: '11223344',
      sortCode: '089999',
      verificationResult: 'no-match',
      verificationCheckedAt: hoursAgo(20),
      status: 'active',
    },
    {
      id: 'ben_sepa',
      companyId: 'company_healthy_ltd',
      name: 'Verlag Kruger GmbH',
      type: 'business',
      country: 'DE',
      currency: 'EUR',
      iban: 'DE44500105175407324931',
      bic: 'INGDDEFF',
      // Confirmation of Payee is a UK domestic scheme; for SEPA it is not
      // available. Represented honestly rather than faked as a match.
      verificationResult: 'unavailable',
      status: 'active',
    },
    {
      id: 'ben_person',
      companyId: 'company_healthy_ltd',
      name: 'Marcus Bell',
      type: 'person',
      country: 'GB',
      currency: 'GBP',
      accountNumber: '80042219',
      sortCode: '110022',
      verificationResult: 'match',
      verificationCheckedAt: daysAgo(30),
      status: 'active',
    },
    {
      id: 'ben_disabled',
      companyId: 'company_healthy_ltd',
      name: 'Old Mill Supplies (closed)',
      type: 'business',
      country: 'GB',
      currency: 'GBP',
      accountNumber: '90001122',
      sortCode: '404040',
      verificationResult: 'no-match',
      status: 'disabled',
    },
  ];
}

/* ----------------------------------------------------------- payments ----- */

export function buildPayments(): Payment[] {
  return [
    {
      id: 'pay_awaiting_approval',
      companyId: 'company_healthy_ltd',
      sourceAccountId: 'acc_gbp_main',
      beneficiaryId: 'ben_exact',
      amount: 1_250_000,
      currency: 'GBP',
      reference: 'INV-4471 packaging',
      requestedExecutionDate: daysAhead(0),
      status: 'awaiting-approval',
      approvalIds: ['apr_pending_high'],
      payeeVerification: { result: 'match', checkedAt: hoursAgo(2) },
      createdBy: 'user_finance',
      createdAt: hoursAgo(2),
    },
    {
      id: 'pay_awaiting_approval_close',
      companyId: 'company_healthy_ltd',
      sourceAccountId: 'acc_gbp_main',
      beneficiaryId: 'ben_close',
      amount: 486_000,
      currency: 'GBP',
      reference: 'Haulage July',
      requestedExecutionDate: daysAhead(0),
      status: 'awaiting-approval',
      approvalIds: ['apr_pending_close'],
      payeeVerification: {
        result: 'close-match',
        checkedAt: hoursAgo(5),
        proceededDespiteWarning: true,
      },
      createdBy: 'user_finance',
      createdAt: hoursAgo(5),
    },
    {
      id: 'pay_completed',
      companyId: 'company_healthy_ltd',
      sourceAccountId: 'acc_gbp_main',
      beneficiaryId: 'ben_person',
      amount: 92_500,
      currency: 'GBP',
      reference: 'Consultancy June',
      requestedExecutionDate: daysAgo(4),
      status: 'completed',
      approvalIds: [],
      payeeVerification: { result: 'match', checkedAt: daysAgo(4) },
      createdBy: 'user_finance',
      createdAt: daysAgo(4),
    },
    {
      id: 'pay_failed',
      companyId: 'company_healthy_ltd',
      sourceAccountId: 'acc_gbp_main',
      beneficiaryId: 'ben_nomatch',
      amount: 214_000,
      currency: 'GBP',
      reference: 'Components order 88',
      requestedExecutionDate: daysAgo(1),
      status: 'failed',
      approvalIds: [],
      failureReason:
        'The receiving bank rejected the payment: the account details could not be matched.',
      payeeVerification: {
        result: 'no-match',
        checkedAt: daysAgo(1),
        proceededDespiteWarning: true,
      },
      createdBy: 'user_finance',
      createdAt: daysAgo(1),
    },
    {
      id: 'pay_processing',
      companyId: 'company_healthy_ltd',
      sourceAccountId: 'acc_eur_trade',
      beneficiaryId: 'ben_sepa',
      amount: 340_000,
      currency: 'EUR',
      reference: 'Print run Q3',
      requestedExecutionDate: hoursAgo(1),
      status: 'processing',
      approvalIds: [],
      createdBy: 'user_owner',
      createdAt: hoursAgo(1),
    },
    {
      id: 'pay_scheduled',
      companyId: 'company_healthy_ltd',
      sourceAccountId: 'acc_gbp_main',
      beneficiaryId: 'ben_exact',
      amount: 175_000,
      currency: 'GBP',
      reference: 'Monthly retainer',
      requestedExecutionDate: daysAhead(5),
      status: 'scheduled',
      approvalIds: [],
      createdBy: 'user_owner',
      createdAt: daysAgo(9),
    },
    {
      id: 'pay_returned',
      companyId: 'company_healthy_ltd',
      sourceAccountId: 'acc_gbp_main',
      beneficiaryId: 'ben_disabled',
      amount: 61_200,
      currency: 'GBP',
      reference: 'Final invoice',
      requestedExecutionDate: daysAgo(16),
      status: 'returned',
      approvalIds: [],
      failureReason: 'Returned by the beneficiary bank: account closed.',
      createdBy: 'user_finance',
      createdAt: daysAgo(16),
    },
  ];
}

export function buildPaymentAttempts(): PaymentAttempt[] {
  return [
    {
      id: 'att_failed_1',
      paymentId: 'pay_failed',
      attemptNumber: 1,
      status: 'failed',
      providerReference: 'TRB-ATT-90112',
      failureReason: 'Beneficiary account details could not be matched.',
      startedAt: daysAgo(1),
      completedAt: daysAgo(1),
    },
    {
      id: 'att_completed_1',
      paymentId: 'pay_completed',
      attemptNumber: 1,
      status: 'succeeded',
      providerReference: 'TRB-ATT-88431',
      startedAt: daysAgo(4),
      completedAt: daysAgo(4),
    },
    {
      id: 'att_processing_1',
      paymentId: 'pay_processing',
      attemptNumber: 1,
      status: 'processing',
      providerReference: 'TRB-ATT-91277',
      startedAt: hoursAgo(1),
    },
  ];
}

export function buildApprovals(): Approval[] {
  return [
    {
      id: 'apr_pending_high',
      objectType: 'payment',
      objectId: 'pay_awaiting_approval',
      status: 'pending',
      requiredApprovals: 1,
      receivedApprovals: 0,
      approverIds: ['user_owner', 'user_approver'],
      thresholdAmount: 1_000_000,
    },
    {
      id: 'apr_pending_close',
      objectType: 'payment',
      objectId: 'pay_awaiting_approval_close',
      status: 'pending',
      requiredApprovals: 1,
      receivedApprovals: 0,
      approverIds: ['user_owner', 'user_approver'],
      thresholdAmount: 250_000,
    },
    {
      // Already actioned by someone else — proves the already-actioned state.
      id: 'apr_already_actioned',
      objectType: 'payment',
      objectId: 'pay_completed',
      status: 'approved',
      requiredApprovals: 1,
      receivedApprovals: 1,
      approverIds: ['user_owner'],
    },
  ];
}

/* ---------------------------------------------------------------- cards --- */

export function buildCards(): Card[] {
  return [
    {
      id: 'card_active_physical',
      companyId: 'company_healthy_ltd',
      accountId: 'acc_gbp_expense',
      assignedUserId: 'user_owner',
      cardholderName: 'PRIYA RAMAN',
      last4: '4417',
      cardType: 'physical',
      lifecycleStatus: 'active',
      fulfilmentStatus: 'delivered',
      replacementStatus: 'not-requested',
      spendingLimit: 500_000,
      currency: 'GBP',
      controls: { online: true, contactless: true, atm: true, international: false },
    },
    {
      id: 'card_virtual_active',
      companyId: 'company_healthy_ltd',
      accountId: 'acc_gbp_expense',
      assignedUserId: 'user_finance',
      cardholderName: 'DANIEL OKAFOR',
      last4: '8802',
      cardType: 'virtual',
      lifecycleStatus: 'active',
      fulfilmentStatus: 'not-required',
      replacementStatus: 'not-requested',
      spendingLimit: 150_000,
      currency: 'GBP',
      controls: { online: true, contactless: false, atm: false, international: true },
    },
    {
      id: 'card_frozen',
      companyId: 'company_healthy_ltd',
      accountId: 'acc_gbp_expense',
      assignedUserId: 'user_employee',
      cardholderName: 'TOM WHITFIELD',
      last4: '2290',
      cardType: 'physical',
      lifecycleStatus: 'frozen',
      fulfilmentStatus: 'delivered',
      replacementStatus: 'not-requested',
      spendingLimit: 75_000,
      currency: 'GBP',
      controls: { online: false, contactless: true, atm: false, international: false },
      statusReason: 'You froze this card on 24 July.',
    },
    {
      id: 'card_blocked_by_ops',
      companyId: 'company_healthy_ltd',
      accountId: 'acc_gbp_expense',
      cardholderName: 'NORTHGATE SPARE',
      last4: '7731',
      cardType: 'physical',
      lifecycleStatus: 'blocked',
      fulfilmentStatus: 'delivered',
      replacementStatus: 'requested',
      spendingLimit: 100_000,
      currency: 'GBP',
      controls: { online: false, contactless: false, atm: false, international: false },
      // Operator-blocked: show reason, hide the customer unfreeze control.
      statusReason:
        'Blocked by your bank following suspected fraud. Contact support — this cannot be undone in the app.',
    },
    {
      id: 'card_ordered',
      companyId: 'company_healthy_ltd',
      accountId: 'acc_gbp_expense',
      assignedUserId: 'user_approver',
      cardholderName: 'HELENA VASQUEZ',
      last4: '5108',
      cardType: 'physical',
      lifecycleStatus: 'ordered',
      fulfilmentStatus: 'shipped',
      replacementStatus: 'not-requested',
      spendingLimit: 200_000,
      currency: 'GBP',
      controls: { online: false, contactless: false, atm: false, international: false },
    },
    {
      id: 'card_expired',
      companyId: 'company_healthy_ltd',
      accountId: 'acc_gbp_expense',
      cardholderName: 'NORTHGATE OLD',
      last4: '3364',
      cardType: 'physical',
      lifecycleStatus: 'expired',
      fulfilmentStatus: 'delivered',
      replacementStatus: 'completed',
      spendingLimit: 0,
      currency: 'GBP',
      controls: { online: false, contactless: false, atm: false, international: false },
    },
  ];
}

/* --------------------------------------------------------- transactions --- */

type TxSeed = {
  id: string;
  accountId: string;
  type: Transaction['type'];
  direction: Transaction['direction'];
  amount: number;
  currency: string;
  status: Transaction['status'];
  counterparty: string;
  hoursAgo: number;
  reference?: string;
  category?: string;
  merchantName?: string;
  merchantCategoryCode?: string;
  relatedPaymentId?: string;
  relatedCardId?: string;
  feeAmount?: number;
  fxRateApplied?: number;
};

const HEALTHY_TX: readonly TxSeed[] = [
  { id: 'tx_001', accountId: 'acc_gbp_main', type: 'bank-transfer', direction: 'in', amount: 1_845_000, currency: 'GBP', status: 'completed', counterparty: 'Braithwaite Retail Group', hoursAgo: 3, reference: 'INV-4468' },
  { id: 'tx_002', accountId: 'acc_gbp_expense', type: 'card-purchase', direction: 'out', amount: 4_260, currency: 'GBP', status: 'completed', counterparty: 'Pret A Manger', hoursAgo: 5, merchantName: 'Pret A Manger', merchantCategoryCode: '5812', category: 'Meals', relatedCardId: 'card_active_physical' },
  { id: 'tx_003', accountId: 'acc_gbp_main', type: 'bank-transfer', direction: 'out', amount: 92_500, currency: 'GBP', status: 'completed', counterparty: 'Marcus Bell', hoursAgo: 96, reference: 'Consultancy June', relatedPaymentId: 'pay_completed' },
  { id: 'tx_004', accountId: 'acc_gbp_main', type: 'direct-debit', direction: 'out', amount: 31_800, currency: 'GBP', status: 'completed', counterparty: 'British Gas Business', hoursAgo: 30, reference: 'DD 8871002' },
  { id: 'tx_005', accountId: 'acc_gbp_expense', type: 'card-purchase', direction: 'out', amount: 12_999, currency: 'GBP', status: 'pending', counterparty: 'Amazon Business', hoursAgo: 1, merchantName: 'Amazon Business', merchantCategoryCode: '5943', category: 'Office supplies', relatedCardId: 'card_virtual_active' },
  { id: 'tx_006', accountId: 'acc_eur_trade', type: 'fx', direction: 'in', amount: 640_000, currency: 'EUR', status: 'completed', counterparty: 'FX conversion GBP→EUR', hoursAgo: 52, fxRateApplied: 1.1642, feeAmount: 1_200 },
  { id: 'tx_007', accountId: 'acc_gbp_main', type: 'bank-transfer', direction: 'out', amount: 214_000, currency: 'GBP', status: 'failed', counterparty: 'Delta Components', hoursAgo: 24, reference: 'Components order 88', relatedPaymentId: 'pay_failed' },
  { id: 'tx_008', accountId: 'acc_gbp_main', type: 'bank-transfer', direction: 'out', amount: 61_200, currency: 'GBP', status: 'reversed', counterparty: 'Old Mill Supplies', hoursAgo: 384, reference: 'Final invoice', relatedPaymentId: 'pay_returned' },
  { id: 'tx_009', accountId: 'acc_gbp_main', type: 'fee', direction: 'out', amount: 950, currency: 'GBP', status: 'completed', counterparty: 'Monthly account fee', hoursAgo: 168 },
  { id: 'tx_010', accountId: 'acc_gbp_main', type: 'bank-transfer', direction: 'in', amount: 428_000, currency: 'GBP', status: 'completed', counterparty: 'Kestrel Foods Ltd', hoursAgo: 200, reference: 'INV-4455' },
  { id: 'tx_011', accountId: 'acc_gbp_expense', type: 'card-purchase', direction: 'out', amount: 8_640, currency: 'GBP', status: 'completed', counterparty: 'Trainline', hoursAgo: 74, merchantName: 'Trainline', merchantCategoryCode: '4112', category: 'Travel', relatedCardId: 'card_active_physical' },
  { id: 'tx_012', accountId: 'acc_eur_trade', type: 'bank-transfer', direction: 'out', amount: 340_000, currency: 'EUR', status: 'pending', counterparty: 'Verlag Kruger GmbH', hoursAgo: 1, reference: 'Print run Q3', relatedPaymentId: 'pay_processing' },
  { id: 'tx_013', accountId: 'acc_usd_supplier', type: 'bank-transfer', direction: 'in', amount: 512_300, currency: 'USD', status: 'completed', counterparty: 'Pacific Rim Trading', hoursAgo: 300, reference: 'PO-2201' },
  { id: 'tx_014', accountId: 'acc_gbp_main', type: 'bank-transfer', direction: 'in', amount: 96_400, currency: 'GBP', status: 'completed', counterparty: 'Stonebridge Cafes', hoursAgo: 340, reference: 'INV-4440' },
  { id: 'tx_015', accountId: 'acc_gbp_expense', type: 'card-purchase', direction: 'out', amount: 3_450, currency: 'GBP', status: 'completed', counterparty: 'Uber', hoursAgo: 120, merchantName: 'Uber', merchantCategoryCode: '4121', category: 'Travel', relatedCardId: 'card_frozen' },
];

function seedToTransaction(s: TxSeed, companyId: string): Transaction {
  const occurredAt = hoursAgo(s.hoursAgo);
  return {
    id: s.id,
    companyId,
    accountId: s.accountId,
    type: s.type,
    direction: s.direction,
    amount: s.amount,
    currency: s.currency,
    status: s.status,
    counterparty: s.counterparty,
    occurredAt,
    ...(s.status === 'completed' ? { bookedAt: occurredAt } : {}),
    ...(s.reference !== undefined ? { reference: s.reference } : {}),
    ...(s.category !== undefined ? { category: s.category } : {}),
    ...(s.merchantName !== undefined ? { merchantName: s.merchantName } : {}),
    ...(s.merchantCategoryCode !== undefined ? { merchantCategoryCode: s.merchantCategoryCode } : {}),
    ...(s.relatedPaymentId !== undefined ? { relatedPaymentId: s.relatedPaymentId } : {}),
    ...(s.relatedCardId !== undefined ? { relatedCardId: s.relatedCardId } : {}),
    ...(s.feeAmount !== undefined ? { feeAmount: s.feeAmount } : {}),
    ...(s.fxRateApplied !== undefined ? { fxRateApplied: s.fxRateApplied } : {}),
  };
}

/** Deterministic pseudo-random walk for the high-volume pagination fixture. */
function highVolumeTransactions(count: number): Transaction[] {
  const names = [
    'Meridian Retail', 'Halden Foods', 'Pinegate Ltd', 'Corvus Supplies', 'Vantage Print',
    'Ridgeway Fuel', 'Selwyn Trading', 'Orbit Media', 'Thameside Ltd', 'Quill & Co',
  ];
  const out: Transaction[] = [];
  let seed = 20260727;
  const next = () => {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    return seed / 2147483648;
  };
  for (let i = 0; i < count; i += 1) {
    const r = next();
    const inbound = r > 0.55;
    const nameIndex = Math.floor(next() * names.length);
    const occurredAt = hoursAgo(i * 3 + 2);
    out.push({
      id: `tx_hv_${String(i + 1).padStart(4, '0')}`,
      companyId: 'company_high_volume_ltd',
      accountId: 'acc_hv_gbp',
      type: 'bank-transfer',
      direction: inbound ? 'in' : 'out',
      amount: Math.round(next() * 480_000) + 2_500,
      currency: 'GBP',
      status: 'completed',
      counterparty: names[nameIndex] ?? 'Meridian Retail',
      reference: `REF-${String(90_000 + i)}`,
      occurredAt,
      bookedAt: occurredAt,
    });
  }
  return out;
}

export function buildTransactions(): Transaction[] {
  return [
    ...HEALTHY_TX.map((s) => seedToTransaction(s, 'company_healthy_ltd')),
    {
      id: 'tx_res_001',
      companyId: 'company_restricted_ltd',
      accountId: 'acc_res_gbp',
      type: 'bank-transfer',
      direction: 'in',
      amount: 1_902_450,
      currency: 'GBP',
      status: 'completed',
      counterparty: 'Copperfield Client Ltd',
      reference: 'Settlement',
      occurredAt: daysAgo(11),
      bookedAt: daysAgo(11),
    },
    ...highVolumeTransactions(240),
  ];
}

/* -------------------------------------------------------- feature flags --- */

export function buildFeatureFlags(): SmeFeatureFlags {
  return {
    accounts: true,
    transactions: true,
    beneficiaries: true,
    payments: true,
    approvals: true,
    cards: true,
    fx: true,
    directDebits: true,
    reports: true,
    teamManagement: true,
    expenseEntryPoints: true,
    aiFinancialSummary: true,
    aiTransactionSearch: true,
    aiAttention: true,
    aiPaymentPreparation: true,
    exchangePlacement: 'accounts-header',
    addAccountControl: true,
  };
}

/* -------------------------------------------------------- audit history --- */

/**
 * Prior customer-visible history.
 *
 * TRACE:TT-R126-02 — the all-activity view must render activity records
 * (populated) OR an explicit empty state. Without seeded history the populated
 * branch was unreachable, so only the empty state could ever be shown or tested.
 */
export function buildAuditHistory(): AuditEvent[] {
  const seed = (
    n: number,
    hours: number,
    actorName: string,
    action: string,
    objectType: string,
    objectId: string,
    summary: string,
  ): AuditEvent => ({
    id: `evt_seed_${String(n).padStart(3, '0')}`,
    companyId: 'company_healthy_ltd',
    programmeId: PROGRAMME.id,
    actorName,
    actorType: 'customer',
    action,
    objectType,
    objectId,
    createdAt: hoursAgo(hours),
    customerVisible: true,
    immutable: true,
    metadata: { summary },
  });

  return [
    seed(1, 300, 'Priya Raman', 'account.renamed', 'account', 'acc_eur_trade', 'Renamed account to “EUR trade”'),
    seed(2, 240, 'Daniel Okafor', 'beneficiary.created', 'beneficiary', 'ben_exact', 'Added beneficiary Ashgrove Packaging Ltd'),
    seed(3, 200, 'Daniel Okafor', 'payment.submitted', 'payment', 'pay_completed', 'Payment sent'),
    seed(4, 196, 'Priya Raman', 'approval.approved', 'payment', 'pay_completed', 'Approved a payment'),
    seed(5, 120, 'Priya Raman', 'card.controls-changed', 'card', 'card_active_physical', 'Updated spending controls on card ending 4417'),
    seed(6, 72, 'Tom Whitfield', 'card.freeze', 'card', 'card_frozen', 'Card ending 2290: freeze'),
    seed(7, 48, 'Priya Raman', 'device.revoked', 'device', 'dev_old_tablet', 'Signed out iPad Air'),
    seed(8, 26, 'Daniel Okafor', 'payment.failed', 'payment', 'pay_failed', 'Payment failed'),
  ];
}

/* ------------------------------------------------------------ notifications */

export type Notification = {
  readonly id: string;
  readonly companyId: string;
  readonly kind: 'payment' | 'card' | 'risk' | 'approval' | 'account' | 'system';
  readonly title: string;
  readonly body: string;
  readonly createdAt: string;
  readonly read: boolean;
  /** Deep link target inside the mobile app. */
  readonly route: string;
};

export function buildNotifications(): Notification[] {
  return [
    {
      id: 'ntf_approval',
      companyId: 'company_healthy_ltd',
      kind: 'approval',
      title: 'Payment needs your approval',
      body: '£12,500.00 to Ashgrove Packaging Ltd is waiting for a decision.',
      createdAt: hoursAgo(2),
      read: false,
      route: '/mobile/approvals/apr_pending_high',
    },
    {
      id: 'ntf_failed',
      companyId: 'company_healthy_ltd',
      kind: 'payment',
      title: 'Payment failed',
      body: '£2,140.00 to Delta Components was rejected by the receiving bank.',
      createdAt: daysAgo(1),
      read: false,
      route: '/mobile/payments/pay_failed',
    },
    {
      id: 'ntf_card_block',
      companyId: 'company_healthy_ltd',
      kind: 'risk',
      title: 'Card blocked by your bank',
      body: 'Card ending 7731 was blocked following suspected fraud.',
      createdAt: daysAgo(2),
      read: false,
      route: '/mobile/cards/card_blocked_by_ops',
    },
    {
      id: 'ntf_received',
      companyId: 'company_healthy_ltd',
      kind: 'account',
      title: 'Money received',
      body: '£18,450.00 from Braithwaite Retail Group.',
      createdAt: hoursAgo(3),
      read: true,
      route: '/mobile/transactions/tx_001',
    },
    {
      id: 'ntf_statement',
      companyId: 'company_healthy_ltd',
      kind: 'system',
      title: 'June statement ready',
      body: 'Your monthly statement is available to download.',
      createdAt: daysAgo(26),
      read: true,
      route: '/mobile/reports',
    },
  ];
}
